import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface AccordionContextValue {
  value: string | null;
  onToggle: (value: string) => void;
}

const AccordionContext = React.createContext<AccordionContextValue>({
  value: null,
  onToggle: () => {},
});

interface AccordionProps {
  type?: "single";
  collapsible?: boolean;
  className?: string;
  children: React.ReactNode;
}

const Accordion = ({ className, children }: AccordionProps) => {
  const [value, setValue] = React.useState<string | null>(null);
  const onToggle = React.useCallback((v: string) => {
    setValue((prev) => (prev === v ? null : v));
  }, []);
  return (
    <AccordionContext.Provider value={{ value, onToggle }}>
      <div className={className}>{children}</div>
    </AccordionContext.Provider>
  );
};

interface AccordionItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, className, children, ...props }, ref) => {
    const ctx = React.useContext(AccordionContext);
    const isOpen = ctx.value === value;
    return (
      <div
        ref={ref}
        data-state={isOpen ? "open" : "closed"}
        className={cn("border-b", className)}
        {...props}
      >
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<any>, {
                __isOpen: isOpen,
                __value: value,
                __onToggle: ctx.onToggle,
              })
            : child
        )}
      </div>
    );
  }
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerInternalProps {
  className?: string;
  children: React.ReactNode;
  __isOpen?: boolean;
  __value?: string;
  __onToggle?: (v: string) => void;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerInternalProps>(
  ({ className, children, __isOpen, __value, __onToggle, ...props }, ref) => (
    <div className="flex">
      <button
        ref={ref}
        type="button"
        aria-expanded={__isOpen}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
          className
        )}
        onClick={() => __onToggle?.(__value!)}
        {...props}
      >
        {children}
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-200",
            __isOpen && "rotate-180"
          )}
        />
      </button>
    </div>
  )
);
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentInternalProps {
  className?: string;
  children: React.ReactNode;
  __isOpen?: boolean;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentInternalProps>(
  ({ className, children, __isOpen, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
        __isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )}
      {...props}
    >
      <div className="overflow-hidden">
        <div className={cn("pb-4 pt-0 text-sm", className)}>{children}</div>
      </div>
    </div>
  )
);
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
